// People's phone numbers in the admin website (guards, supervisors, clients, district managers), in one
// place. The admin types the local number; the company's dialling code is put in front. The server does
// the same when saving (App\People::normalisePhone in Laravel), so the rule holds even without this form.
import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input'
import { auth } from '../Api'

// The dialling code ("254") of a country ("KE"), or null when it is not known.
export function dialCodeFor(country) {
	if (!country) {
		return null;
	}
	try {
		return getCountryCallingCode(String(country).trim().toUpperCase());
	} catch (e) {
		return null;
	}
}

// The dialling code of the company open in the admin website, from its company details (its country).
export function loadCompanyDialCode() {
	const companyId = localStorage.getItem('companyId');
	return auth.getCompany({ id: companyId })
		.then((res) => {
			if (res.data.code == 200 && res.data.message.users && res.data.message.users[0]) {
				return dialCodeFor(res.data.message.users[0].countrycode);
			}
			return null;
		})
		.catch(() => null);
}

// What the admin typed, as it is saved: "+" and digits, or null when it is not a usable number.
// - Starting with "+": a full international number (a foreign one), kept as it is.
// - Otherwise a local number: spaces, dashes and brackets are ignored, leading 0s dropped, and the
//   company's dialling code put in front ("0712 345 678" -> "+254712345678").
// A number must have 7 to 15 digits in the end.
export function toInternational(typed, dialCode) {
	if (typed == null) {
		return null;
	}
	const text = String(typed).trim();
	let digits = text.replace(/\D/g, '');
	if (!text.startsWith('+')) {
		if (!dialCode) {
			return null;
		}
		digits = String(dialCode) + digits.replace(/^0+/, '');
	}
	return digits.length >= 7 && digits.length <= 15 ? '+' + digits : null;
}

// The message for a number toInternational() could not use.
export function phoneProblem(typed, dialCode) {
	const text = typed == null ? '' : String(typed).trim();
	if (text === '') {
		return 'Please enter a phone number.';
	}
	if (!dialCode && !text.startsWith('+')) {
		return "The company has no country in its company details, so the phone number cannot be completed. Add the country, or type the phone number with + and its country code.";
	}
	return 'This phone number is not valid';
}

// A stored number shown for reading, grouped the way the country writes it ("+254 712 345678").
// Anything that cannot be read as an international number is shown as it is.
export function formatPhone(phone) {
	if (phone == null || phone === '') {
		return '';
	}
	const text = String(phone).trim();
	const international = text.startsWith('+') ? text : '+' + text.replace(/\D/g, '');
	try {
		const parsed = parsePhoneNumber(international);
		if (parsed) {
			return parsed.formatInternational();
		}
	} catch (e) {
		// not a number libphonenumber knows; shown as stored
	}
	return text;
}

// A number in the phone field after the fixed "+254" label: a stored number with the company's code
// is shown as its local part ("712 345678"); a foreign one stays in full with its "+".
export function localPart(phone, dialCode) {
	if (phone == null) {
		return '';
	}
	const text = String(phone);
	if (!text.startsWith('+') || !dialCode) {
		return text;
	}
	const prefix = '+' + dialCode;
	if (text.replace(/[\s\-().]/g, '').startsWith(prefix)) {
		const formatted = formatPhone(text);
		if (formatted.startsWith(prefix + ' ')) {
			return formatted.slice(prefix.length + 1);
		}
		return text.replace(/[\s\-().]/g, '').slice(prefix.length);
	}
	return formatPhone(text);
}

// A person's numbers as the form has them (the first is the main one, the rest may be added and removed),
// as they are saved: { phone: the first, phones: all of them ("+" and digits, duplicates dropped),
// problem: null, or the message for the first number that is not usable }. phone is null with a problem.
// An extra number left empty is ignored.
export function phoneListRequest(typed, dialCode) {
	const list = Array.isArray(typed) && typed.length ? typed : [''];
	const phones = [];
	for (let i = 0; i < list.length; i++) {
		const text = list[i] == null ? '' : String(list[i]).trim();
		if (i > 0 && text === '') {
			continue;
		}
		const phone = toInternational(text, dialCode);
		if (phone === null) {
			const problem = phoneProblem(text, dialCode);
			return { phone: null, phones: [], problem: list.length > 1 ? 'Phone number ' + (i + 1) + ': ' + problem : problem };
		}
		if (phones.indexOf(phone) < 0) {
			phones.push(phone);
		}
	}
	return { phone: phones[0], phones: phones, problem: null };
}

// A person's number in a list: the first, with "+N" when they have N more ("+254 712 345678 +1").
export function phoneWithCount(record) {
	const phones = record && Array.isArray(record.phones) && record.phones.length ? record.phones : [record ? record.phone : ''];
	const first = formatPhone(phones[0]);
	return phones.length > 1 ? first + ' +' + (phones.length - 1) : first;
}
